import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  rem,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { IconCheck, IconShieldCheckered } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { userInfoAtom } from "../../atoms";
import { getLinearGradient } from "../../mantineTheme";
import { DiscoverBrands } from "./components/DiscoverBrands";
import { DiscoverCreators } from "./components/DiscoverCreators";

export default function DiscoverPage() {
  const userInfo = useAtomValue(userInfoAtom);
  const isBrand = !!userInfo?.brandId;
  const isLoggedIn = !!userInfo;
  const theme = useMantineTheme();

  // For non-logged-in users, default to "brands" view
  const [view, setView] = useState<"brands" | "creators">("brands");

  // If logged in, show the appropriate view based on user type
  if (isLoggedIn) {
    return isBrand ? <DiscoverCreators /> : <DiscoverBrands />;
  }

  const features = [
    {
      icon: <IconCheck size={rem(24)} />,
      title: "100+ Verified Creators",
      description: "Access a curated network of authentic influencers",
    },
    // {
    //   icon: <IconTrendingUp size={rem(24)} />,
    //   title: "Track Performance",
    //   description: "Monitor ROI and campaign metrics in real-time",
    // },
    {
      icon: <IconShieldCheckered size={rem(24)} />,
      title: "Secure Platform",
      description: "Protected payments and transparent agreements",
    },
  ];

  // Non-logged-in users see a toggle with CTA
  return (
    <Box>
      <Box
        py="xl"
        style={{
          background: `linear-gradient(0deg, ${theme.colors.primary[0]} 0%, white 100%)`,
        }}
      >
        <Container size="xl">
          <Stack gap="lg" align="center">
            <SegmentedControl
              value={view}
              onChange={(value) => setView(value as "brands" | "creators")}
              data={[
                { label: "Discover Brands", value: "brands" },
                { label: "Discover Creators", value: "creators" },
              ]}
              size="md"
              radius="xl"
              color="primary"
            />
          </Stack>
        </Container>
      </Box>

      {view === "brands" ? (
        <DiscoverBrands />
      ) : (
        <Container size="lg" py="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Stack gap="xl" align="center">
              <Stack
                gap="md"
                align="center"
                style={{ textAlign: "center", maxWidth: 600 }}
              >
                <Title
                  order={1}
                  style={{
                    fontSize: rem(48),
                    fontWeight: 900,
                    background: `linear-gradient(45deg, ${theme.colors.primary[6]}, ${theme.colors.tertiary[6]}, ${theme.colors.secondary[6]})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Partner with 100+ Top Creators
                </Title>
                <Text size="xl" c="dimmed">
                  Join Collabish as a brand and connect directly with talented
                  creators who can elevate your products and drive real results.
                </Text>
              </Stack>

              <SimpleGrid
                cols={{ base: 1, sm: 2 }}
                spacing="lg"
                w="100%"
                mt="md"
              >
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Paper p="lg" radius="lg" shadow="sm" withBorder h="100%">
                      <ThemeIcon
                        size={rem(48)}
                        radius="lg"
                        variant="light"
                        color="primary"
                      >
                        {feature.icon}
                      </ThemeIcon>
                      <Text fw={600} mt="md" size="lg">
                        {feature.title}
                      </Text>
                      <Text c="dimmed" mt="xs" size="sm">
                        {feature.description}
                      </Text>
                    </Paper>
                  </motion.div>
                ))}
              </SimpleGrid>

              <Paper
                p="xl"
                radius="lg"
                shadow="md"
                w="100%"
                style={{
                  background: getLinearGradient("primary"),
                  color: "white",
                }}
                mt="xl"
              >
                <Stack gap="lg" align="center" style={{ textAlign: "center" }}>
                  <Title order={2} style={{ color: "white" }}>
                    Ready to Get Started?
                  </Title>
                  <Text size="lg" style={{ maxWidth: 500, color: "white" }}>
                    Create your brand account today and start collaborating with
                    creators who align with your vision.
                  </Text>
                  <Group justify="center" gap="md">
                    <Button
                      component={Link}
                      to="/contact?from=brand"
                      size="lg"
                      radius="xl"
                      variant="white"
                      color="dark"
                    >
                      Get Started as a Brand
                    </Button>
                    <Button
                      component={Link}
                      to="/login"
                      size="lg"
                      radius="xl"
                      variant="outline"
                      color="white"
                      style={{ borderColor: "white", color: "white" }}
                    >
                      Log In
                    </Button>
                  </Group>
                </Stack>
              </Paper>

              <Text size="sm" c="dimmed" mt="md">
                Already a creator?{" "}
                <Text
                  component={Link}
                  to="/signup"
                  span
                  fw={600}
                  style={{ color: theme.colors.primary[6] }}
                >
                  Sign up here
                </Text>
              </Text>
            </Stack>
          </motion.div>
        </Container>
      )}
    </Box>
  );
}
